import { c as _c } from "react/compiler-runtime";
import { Logo } from "@/components/rac/logo";
import { useAuth } from "@/hooks/use-auth";
import { useUserbackFeedback } from "@/hooks/use-userback-feedback";
import { useVersionCheck } from "@/hooks/use-version-check";
import { COMPANY_INFO } from "@/info/company";
import { VERSION } from "@/lib/version";
import { trackContactCall, trackContactMail } from "@/utils/analytics/track-contact";
import { trackEvent } from "@/utils/analytics/track-event";
import type { PropertyLink } from "@/utils/footer-links";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import Link from "next/link";
import { type ReactNode } from "react";
import { Button } from "../rac/button";
import { SocialLinks } from "./social-links";
const companyLinks = [{
  label: "About us",
  href: "/about"
}, {
  label: "Blog",
  href: `${COMPANY_INFO.website}/blog`
}, {
  label: "Careers",
  href: `mailto:${COMPANY_INFO.contact.email.careers}`
}, {
  label: "Contact",
  href: "/contact"
}, {
  label: "Settlin Reviews",
  href: "/reviews"
}, {
  label: "FAQ",
  href: "/faq"
}, {
  label: "Pricing",
  href: "/pricing"
}, {
  label: "Privacy Policy",
  href: "/privacy-policy"
}, {
  label: "Sitemap",
  href: "/sitemap"
}, {
  label: "Terms of use",
  href: "/terms"
}, {
  label: "Features",
  href: "/features"
}];
interface FooterClientProps {
  linkColumns: PropertyLink[][];
  seoContent?: ReactNode;
}
export function FooterClient(t0) {
  const $ = _c(47);
  const {
    linkColumns,
    seoContent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date().getFullYear();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentYear = t1;
  useAuth();
  const {
    hasUpdate,
    serverVersion,
    reloadToUpdate
  } = useVersionCheck(300000);
  const {
    openFeedback: handleFeedbackClick,
    isLoading: isFeedbackLoading
  } = useUserbackFeedback();
  const handleSocialClick = _temp;
  const handleQuickLinkClick = _temp2;
  const handleCompanyLinkClick = _temp3;
  const handleContactClick = _temp4;
  let t2;
  if ($[1] !== seoContent) {
    t2 = seoContent && <section className="bg-gradient-to-r from-[#251821] to-[#31202C]">{seoContent}</section>;
    $[1] = seoContent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="text-2xl md:text-3xl font-bold text-white text-center underline mb-6 md:mb-8">Quick links</h2>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== linkColumns) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = (columnLinks, columnIndex) => <div key={columnIndex} className="flex flex-col gap-2">{columnLinks.map((link, index) => <Link key={index} href={link.href} aria-label={`View properties: ${link.label}`} className="text-sm font-bold text-white hover:underline transition-colors" onClick={() => handleQuickLinkClick(link.label, link.href)}>{link.label}</Link>)}</div>;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = linkColumns.map(t5);
    $[4] = linkColumns;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <section className="bg-[#251821] py-6 md:py-12"><div className="container mx-auto px-4">{t3}<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">{t4}</div></div></section>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="space-y-4"><Logo withName={true} className="h-30 w-auto text-white" /><p className="text-gray-300 text-sm">Your trusted partner in property transactions in Bangalore.</p><SocialLinks handleSocialClick={handleSocialClick} /></div>;
    t7 = <div className="text-lg font-semibold text-white mb-4">Company</div>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = companyLinks.map(link_0 => <li key={link_0.href}><Link href={link_0.href} aria-label={`Go to ${link_0.label}`} className="text-gray-300 hover:text-white text-sm transition-colors" onClick={() => handleCompanyLinkClick(link_0.label, link_0.href)}>{link_0.label}</Link></li>);
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isFeedbackLoading) {
    t9 = isFeedbackLoading && <Loader2 className="w-3 h-3 animate-spin" />;
    $[12] = isFeedbackLoading;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== handleFeedbackClick || $[15] !== isFeedbackLoading || $[16] !== t9) {
    t10 = <div>{t7}<ul className="space-y-2">{t8}<li><Button variant="bareshell" className="p-0 m-0 h-auto text-gray-300 hover:text-white text-sm transition-colors flex items-center gap-2" onClick={handleFeedbackClick} isDisabled={isFeedbackLoading}>{t9}Feedback</Button></li></ul></div>;
    $[14] = handleFeedbackClick;
    $[15] = isFeedbackLoading;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="text-lg font-semibold text-white mb-4">Contact Us</div>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Mail className="h-5 w-5 shrink-0 mt-0.5" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="flex items-start gap-2">{t12}<a target="_blank" rel="noreferer noopener" href={`mailto:${COMPANY_INFO.contact.email.primary}`} onClick={() => handleContactClick("Email")} className="hover:text-white transition-colors">{COMPANY_INFO.contact.email.primary}</a></p>;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Phone className="h-5 w-5 shrink-0 mt-0.5" />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = COMPANY_INFO.contact.phone.primary.replace(/\s|-/g, "");
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="flex items-start gap-2">{t14}<a target="_blank" rel="noreferer noopener" href={`tel:${t15}`} onClick={() => handleContactClick("Phone")} className="hover:text-white transition-colors">{COMPANY_INFO.contact.phone.primary}</a></p>;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <MapPin className="h-5 w-5 shrink-0 mt-0.5" />;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div>{t11}<div className="space-y-2 text-sm text-gray-300">{t13}{t16}<p className="flex items-start gap-2">{t17}<a target="_blank" rel="noreferer noopener" href={COMPANY_INFO.address.googleMapsUrl} onClick={() => handleContactClick("Address")} className="hover:text-white transition-colors">{COMPANY_INFO.address.full}</a></p></div></div>;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t10) {
    t19 = <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">{t6}{t10}{t18}</div>;
    $[26] = t10;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  let t21;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Logo className="h-5 w-auto text-white" />;
    t21 = <span className="text-sm text-gray-400">{currentYear} {COMPANY_INFO.legalName}. All rights reserved. v{VERSION}</span>;
    $[28] = t20;
    $[29] = t21;
  } else {
    t20 = $[28];
    t21 = $[29];
  }
  let t22;
  if ($[30] !== hasUpdate || $[31] !== reloadToUpdate || $[32] !== serverVersion) {
    t22 = hasUpdate && <button onClick={reloadToUpdate} className="text-sm text-primary-foreground bg-primary/80 hover:bg-primary px-2 py-0.5 rounded transition-colors">Update to v{serverVersion}</button>;
    $[30] = hasUpdate;
    $[31] = reloadToUpdate;
    $[32] = serverVersion;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t22) {
    t23 = <div className="flex items-center gap-2" aria-label="Settlin Logo">{t20}{t21}{t22}</div>;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Link href="/terms" aria-label="Read our Terms of Service" className="text-sm text-gray-400 hover:text-white" onClick={() => handleCompanyLinkClick("Terms of Service", "/terms")}>Terms of Service</Link>;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="flex space-x-6 mt-4 md:mt-0">{t24}<Link href="/privacy-policy" aria-label="Read our Privacy Policy" className="text-sm text-gray-400 hover:text-white" onClick={() => handleCompanyLinkClick("Privacy Policy", "/privacy-policy")}>Privacy Policy</Link></div>;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== t23) {
    t26 = <div className="border-t border-gray-700 pt-6 mt-8"><div className="flex flex-col md:flex-row justify-between items-center">{t23}{t25}</div></div>;
    $[38] = t23;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t19 || $[41] !== t26) {
    t27 = <section className="bg-[#31202C] pt-6 md:pt-12 pb-3 md:pb-6"><div className="container mx-auto px-4">{t19}{t26}</div></section>;
    $[40] = t19;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== t2 || $[44] !== t27 || $[45] !== t5) {
    t28 = <footer className="w-full">{t2}{t5}{t27}</footer>;
    $[43] = t2;
    $[44] = t27;
    $[45] = t5;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  return t28;
}
function _temp4(contactType) {
  if (contactType.toLowerCase() === "email") {
    trackContactMail();
  } else {
    if (contactType.toLowerCase() === "phone") {
      trackContactCall();
    }
  }
  trackEvent({
    facebook: {
      event: "Contact",
      options: {
        content_name: `Contact - ${contactType}`,
        content_category: "contact_info"
      }
    },
    google: {
      event: "select_content",
      options: {
        content_type: "contact",
        item_id: contactType.toLowerCase()
      }
    },
    matomo: {
      event: `click on ${contactType.toLowerCase()}`,
      options: {
        category: "contact"
      }
    }
  });
}
function _temp3(linkLabel_0, linkHref_0) {
  trackEvent({
    google: {
      event: "select_content",
      options: {
        content_type: "company_link",
        item_id: linkLabel_0.toLowerCase().replace(/\s+/g, "-"),
        custom_parameters: {
          destination_url: linkHref_0
        }
      }
    },
    matomo: {
      event: "company-link-click",
      options: {
        category: "navigation"
      }
    }
  });
}
function _temp2(linkLabel, linkHref) {
  trackEvent({
    google: {
      event: "select_content",
      options: {
        content_type: "quick_link",
        item_id: linkLabel.toLowerCase().replace(/\s+/g, "-"),
        custom_parameters: {
          destination_url: linkHref
        }
      }
    },
    matomo: {
      event: "quick-link-click",
      options: {
        category: "navigation"
      }
    }
  });
}
function _temp(platform, url) {
  trackEvent({
    facebook: {
      event: "Contact",
      options: {
        content_name: `Social Media - ${platform}`,
        content_category: "social_media"
      }
    },
    google: {
      event: "select_content",
      options: {
        content_type: "social_media",
        item_id: platform.toLowerCase(),
        custom_parameters: {
          destination_url: url
        }
      }
    },
    matomo: {
      event: `click on ${platform.toLowerCase()}`,
      options: {
        category: "contact"
      }
    }
  });
}